import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getMyQuizzes, changePassword } from "../services/api";

// ── Utilidades ────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-PE", { day: "numeric", month: "long", year: "numeric" });
}

function ScoreBadge({ correct, total }) {
  const pct   = total > 0 ? Math.round((correct / total) * 100) : 0;
  const color = pct >= 70 ? "#27ae60" : pct >= 40 ? "#e67e22" : "#c0392b";
  return (
    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1rem", color }}>
      {correct}/{total} <span style={{ fontWeight: 400, fontSize: "0.72rem", color: "var(--text-muted)" }}>({pct}%)</span>
    </span>
  );
}

// ── Detalle de un quiz expandido ──────────────────────────────────
function QuizDetail({ quiz }) {
  const options = ["A", "B", "C", "D"];
  return (
    <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "10px" }}>
      {quiz.questions.map((q, i) => {
        const answered  = q.user_answer !== null && q.user_answer !== undefined;
        const userOpt   = answered ? options[q.user_answer] : null;
        const correctOpt = options[q.correct_index];
        const parsedOptions = typeof q.options === "string" ? JSON.parse(q.options) : q.options;

        return (
          <div key={q.question_id} style={{
            background: q.is_correct ? "rgba(39,174,96,.06)" : q.is_correct === false ? "rgba(192,57,43,.06)" : "var(--bg, #fafcfc)",
            border: `0.5px solid ${q.is_correct ? "#b2dfcb" : q.is_correct === false ? "#f5c6bf" : "var(--border)"}`,
            borderRadius: 7, padding: "0.85rem 1rem",
          }}>
            <p style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.82rem", marginBottom: ".5rem" }}>
              {i + 1}. {q.question}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {parsedOptions.map((opt, idx) => {
                const letter    = options[idx];
                const isCorrect = idx === q.correct_index;
                const isUser    = idx === q.user_answer;
                let bg = "transparent", border = "0.5px solid var(--border)", color = "var(--text)";
                if (isCorrect)       { bg = "rgba(39,174,96,.12)";  border = "0.5px solid #27ae60"; color = "#1a6b3c"; }
                if (isUser && !isCorrect) { bg = "rgba(192,57,43,.12)"; border = "0.5px solid #c0392b"; color = "#922b21"; }

                return (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", borderRadius: 5, background: bg, border, fontSize: "0.8rem", color }}>
                    <span style={{ fontWeight: 700, minWidth: 18 }}>{letter}.</span>
                    <span style={{ flex: 1 }}>{opt}</span>
                    {isCorrect && <span style={{ fontSize: "0.7rem", fontWeight: 700 }}>✓ Correcta</span>}
                    {isUser && !isCorrect && <span style={{ fontSize: "0.7rem", fontWeight: 700 }}>✗ Tu respuesta</span>}
                  </div>
                );
              })}
            </div>
            {!answered && (
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: ".4rem" }}>Sin respuesta</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Sección: Historial de quizzes ─────────────────────────────────
function QuizHistory() {
  const [quizzes,  setQuizzes]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    getMyQuizzes()
      .then(setQuizzes)
      .catch(() => setError("No se pudo cargar el historial."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[1,2,3].map((i) => <div key={i} className="skel-line" style={{ height: 56, borderRadius: 8 }} />)}
    </div>
  );

  if (error) return <p style={{ color: "#c0392b", fontSize: "0.85rem" }}>{error}</p>;

  if (quizzes.length === 0) return (
    <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
      Aún no has respondido ningún cuestionario.{" "}
      <Link to="/clases" style={{ color: "var(--teal)" }}>Ver clases →</Link>
    </p>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {quizzes.map((q) => {
        const isOpen = expanded === q.quiz_id;
        const pct    = q.total > 0 ? Math.round((q.correct / q.total) * 100) : 0;
        const barColor = pct >= 70 ? "#27ae60" : pct >= 40 ? "#e67e22" : "#c0392b";

        return (
          <div key={q.quiz_id} style={{ border: "0.5px solid var(--border)", borderRadius: 9, overflow: "hidden", background: "var(--white)" }}>
            {/* Cabecera */}
            <button
              type="button"
              onClick={() => setExpanded(isOpen ? null : q.quiz_id)}
              style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "1rem 1.2rem", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.88rem", color: "var(--teal-dark)", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {q.class_title}
                </p>
                <p style={{ fontFamily: "var(--font-display)", fontSize: "0.72rem", color: "var(--text-muted)", margin: "2px 0 0" }}>
                  {q.quiz_title} · {formatDate(q.last_attempt)}
                </p>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "1.2rem", flexShrink: 0 }}>
                {/* Mini barra de progreso */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
                  <ScoreBadge correct={q.correct} total={q.total} />
                  <div style={{ width: 80, height: 4, background: "#e8edf0", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: 2, transition: "width .3s" }} />
                  </div>
                </div>
                {/* Resumen rápido */}
                <div style={{ display: "flex", gap: "0.6rem", fontSize: "0.72rem", fontFamily: "var(--font-display)" }}>
                  <span style={{ color: "#27ae60", fontWeight: 700 }}>✓{q.correct}</span>
                  <span style={{ color: "#c0392b", fontWeight: 700 }}>✗{q.wrong}</span>
                  {q.empty > 0 && <span style={{ color: "var(--text-muted)" }}>—{q.empty}</span>}
                </div>
                <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>{isOpen ? "▲" : "▼"}</span>
              </div>
            </button>

            {/* Detalle expandible */}
            {isOpen && (
              <div style={{ padding: "0 1.2rem 1.2rem", borderTop: "0.5px solid var(--border)" }}>
                <QuizDetail quiz={q} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Sección: Cambio de contraseña ─────────────────────────────────
function ChangePasswordForm() {
  const [form,    setForm]    = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!form.current_password || !form.new_password || !form.confirm)
      return setError("Todos los campos son requeridos.");
    if (form.new_password.length < 6)
      return setError("La nueva contraseña debe tener al menos 6 caracteres.");
    if (form.new_password !== form.confirm)
      return setError("Las contraseñas nuevas no coinciden.");

    setSaving(true);
    try {
      await changePassword({ current_password: form.current_password, new_password: form.new_password });
      setSuccess("Contraseña actualizada correctamente.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 420 }}>
      {[
        { name: "current_password", label: "Contraseña actual" },
        { name: "new_password",     label: "Nueva contraseña" },
        { name: "confirm",          label: "Confirmar nueva contraseña" },
      ].map(({ name, label }) => (
        <div key={name} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontFamily: "var(--font-display)", fontSize: "0.73rem", color: "var(--text-muted)" }}>{label}</label>
          <input
            type="password" name={name} value={form[name]} onChange={handleChange}
            style={{ fontFamily: "var(--font-display)", fontSize: "0.85rem", padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 5, color: "var(--text)", background: "var(--white)", outline: "none" }}
          />
        </div>
      ))}

      {error   && <p style={{ color: "#c0392b", fontSize: "0.78rem" }}>{error}</p>}
      {success && <p style={{ color: "#27ae60", fontSize: "0.78rem" }}>{success}</p>}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="submit" disabled={saving} style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.82rem", padding: "7px 20px", borderRadius: 5, border: "none", background: "var(--teal)", color: "#fff", cursor: "pointer" }}>
          {saving ? "Guardando..." : "Cambiar contraseña"}
        </button>
      </div>
    </form>
  );
}

// ══════════════════════════════════════════════════════════════════
export default function Profile() {
  const { user, loading } = useAuth();
  const navigate          = useNavigate();
  const [section, setSection] = useState("quizzes");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  if (loading) return null;

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  const SECTIONS = [
    { key: "quizzes",  label: "Mis cuestionarios" },
    { key: "password", label: "Cambiar contraseña" },
  ];

  return (
    <div className="page">
      <div className="page-hero">
        <h1>Mi perfil</h1>
      </div>

      <div style={{ maxWidth: 820, margin: "0 auto", padding: "2rem 1.2rem" }}>

        {/* ── Tarjeta de usuario ── */}
        <div className="card" style={{ display: "flex", alignItems: "center", gap: "1.2rem", marginBottom: "1.5rem" }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.1rem", color: "#fff", flexShrink: 0 }}>
            {initials}
          </div>
          <div>
            <p style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1rem", color: "var(--teal-dark)", margin: 0 }}>{user?.name}</p>
            <p style={{ fontFamily: "var(--font-display)", fontSize: "0.78rem", color: "var(--text-muted)", margin: "2px 0 0" }}>{user?.email}</p>
          </div>
          <span style={{ marginLeft: "auto", fontFamily: "var(--font-display)", fontSize: "0.7rem", fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "var(--teal-light, #e1f5ee)", color: "var(--teal-dark)", textTransform: "uppercase", letterSpacing: ".04em" }}>
            {user?.role === "student" ? "Alumno" : user?.role}
          </span>
        </div>

        {/* ── Tabs de sección ── */}
        <div style={{ display: "flex", gap: ".4rem", marginBottom: "1.2rem", borderBottom: "0.5px solid var(--border)", paddingBottom: ".6rem" }}>
          {SECTIONS.map((s) => (
            <button
              key={s.key} type="button"
              onClick={() => setSection(s.key)}
              style={{ fontFamily: "var(--font-display)", fontWeight: section === s.key ? 700 : 400, fontSize: "0.82rem", padding: "5px 14px", borderRadius: 5, border: "none", cursor: "pointer", background: section === s.key ? "var(--teal)" : "none", color: section === s.key ? "#fff" : "var(--text-muted)", transition: "all .12s" }}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* ── Contenido de sección ── */}
        {section === "quizzes"  && <QuizHistory />}
        {section === "password" && (
          <div className="card" style={{ maxWidth: 460 }}>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "0.95rem", color: "var(--teal-dark)", marginBottom: "1rem" }}>
              Cambiar contraseña
            </h2>
            <ChangePasswordForm />
          </div>
        )}
      </div>
    </div>
  );
}
